import { Box, Button, Checkbox, CircularProgress, Radio, Slide, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import React, { useEffect, useRef, useState } from 'react'

import thinkingSimba from '../../../../../shared/assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { Icons } from '../../../../../shared/utils/icons.jsx'
import { ALPHABETS } from '../../../../../shared/utils/constants.jsx'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import ReportIssue from '../ReportIssue'
import CustomChip from '../../../../../shared/components/common/CustomChip'

const QuizQuestion = observer( () => {

    const [submitting, setSubmitting] = useState( false )

    const { layout, palette, border } = useTheme()
    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const quizContainerRef = useRef()

    const backToHome = async () => {
        SimbaAIStore.setView( "home", "right" )
        setSearchParamsValues( { practiceQuiz: "" } )
        try {
            await window.document.exitFullscreen()
        } catch ( err ) {

        }
    }

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const nextQuestion = () => {
        let nextIndex = SimbaAIStore.getQuizState.questionIndex + 1
        if ( nextIndex >= SimbaAIStore.getPracticeSessionQuestions.length )
            return
        SimbaAIStore.setQuizState( { questionIndex: nextIndex } )
    }

    const prevQuestion = () => {
        let prevIndex = SimbaAIStore.getQuizState.questionIndex - 1
        if ( prevIndex < 0 )
            return
        SimbaAIStore.setQuizState( { questionIndex: prevIndex } )
    }

    const selectAnswer = async answer => {
        const { questionIndex, userAnswers } = SimbaAIStore.getQuizState

        const question = SimbaAIStore.getPracticeSessionQuestions[SimbaAIStore.getQuizState.questionIndex]

        const newAnswers = [...userAnswers]

        if ( question.correct_answers.length > 1 ) {

            if ( !Array.isArray( newAnswers[questionIndex] ) )
                newAnswers[questionIndex] = []

            const answerIndex = newAnswers[questionIndex].findIndex( ans => ans === answer )
            if ( answerIndex >= 0 ) {
                newAnswers[questionIndex].splice( answerIndex, 1 )
            } else {
                newAnswers[questionIndex].push( answer )
            }

            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        } else {
            newAnswers[questionIndex] = answer
            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        }
    }

    const submitAndViewScores = async () => {
        try {
            setSubmitting( true )
            const { userAnswers } = SimbaAIStore.getQuizState
            const response = await SimbaAIStore.updatePracticeData( { user_answers: userAnswers } )
            if ( response ) {
                SimbaAIStore.playSound( "practiceCompleted" )
                SimbaAIStore.setView( "result", "left", false )
                setSearchParamsValues( { practiceQuiz: "" } )
                if ( window.document.fullscreenElement )
                    window.document.exitFullscreen()
            }
        } catch ( err ) {
            console.log( err )
        } finally {
            setSubmitting( false )
        }
    }

    const questionDetails = SimbaAIStore.getPracticeSessionQuestions[SimbaAIStore.getQuizState.questionIndex]



    return (
        <Box
            ref={quizContainerRef}
            sx={{
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: { md: "flex-start", xs: "flex-start" },
                gap: layout.cardsGap,
                overflowX: "hidden",
                overflowY: "auto",
                background: "inherit"
            }}>

            <Box
                sx={{
                    flexShrink: 0,
                    margin: layout.pagePadding
                }}>
                {/* <Typography fontWeight="800" gutterBottom fontSize="14px">{SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.title}</Typography> */}
                <Typography
                    gutterBottom
                    sx={{
                        fontWeight: "800",
                        fontSize: "14px"
                    }}>Question {SimbaAIStore.getQuizState.questionIndex + 1} off {SimbaAIStore.getPracticeSessionQuestions.length}</Typography>
                <Box
                    sx={{
                        overflow: "clip",
                        height: "16px",
                        borderRadius: "20px",
                        bgcolor: palette.greyed.light + "55"
                    }}>
                    <Box
                        className="animation-wave"
                        sx={{
                            borderRadius: "100px",
                            height: "100%",
                            width: `${( 100 * ( SimbaAIStore.getQuizState.questionIndex ) ) / SimbaAIStore.getPracticeSessionQuestions.length}%`,
                            background: palette.primary.dark,
                            transition: "width 400ms",
                            position: "relative",

                            ":before": {
                                content: `''`,
                                borderRadius: "100px",
                                height: "3px",
                                width: "calc(100% - 10px)",
                                top: "4px",
                                left: "5px",
                                position: "absolute",
                                background: `#ffffff`,
                                transition: "width 400ms",
                                opacity: 0.2
                            }
                        }} />
                </Box>
            </Box>

            <Slide in direction='left' key={SimbaAIStore.getQuizState.questionIndex} >
                <Box
                    sx={{
                        overflow: "auto",
                        flexShrink: 0,
                        padding: layout.pagePadding,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "center", md: "initial" },
                        gap: "40px",
                        flexGrow: 1,
                        flexDirection: { xs: "column", sm: "row" }
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            gap: layout.cardsGap,
                            alignItems: "center"
                        }}>
                        <Box component="img" src={thinkingSimba} sx={{
                            width: { md: "20vw", xs: "15vw" }
                        }} />
                        <Typography
                            component="pre"
                            gutterBottom
                            sx={{
                                borderRadius: "50%",
                                display: { md: "none", xs: "block" },
                                fontSize: "18px",
                                fontWeight: "800",
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>{questionDetails.question}</Typography>
                    </Box>
                    <Box
                        sx={{
                            minWidth: 0,
                            flexGrow: 1,
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column"
                        }}>
                        <Typography
                            component="pre"
                            gutterBottom
                            sx={{
                                borderRadius: "50%",
                                display: { md: "block", xs: "none" },
                                fontSize: "26px",
                                fontWeight: "800",
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                userSelect: "none"
                            }}>
                            {questionDetails.question}
                        </Typography>

                        {questionDetails.correct_answers?.length > 1 && <Box sx={{
                            marginBottom: "20px"
                        }}>
                            <CustomChip startIcon={Icons.default.Info} variant='filled' color="customThemeColor" value="Choose multiple correct answers." />
                        </Box>}

                        <Box
                            sx={{
                                flexGrow: 1,
                                minWidth: 0,
                                display: "flex",
                                gap: "10px",
                                flexDirection: "column"
                            }}>
                            {questionDetails.answers.map( ( option, index ) => {

                                const selected = SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] === option || ( Array.isArray( SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ) && SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex]?.includes( option ) )

                                return (
                                    <Box
                                        component="button"
                                        className={selected ? "animation-wave" : ""}
                                        key={option}
                                        onClick={() => selectAnswer( option )}
                                        sx={{
                                            width: { sm: "fit-content", xs: "auto" },
                                            padding: { md: "10px 20px", xs: "10px" },
                                            borderRadius: "10px",
                                            border: `2px solid ${palette.borderColor}`,
                                            borderBottom: `4px solid ${palette.borderColor}`,
                                            color: palette.text.secondary,
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "flex-start",
                                            height: "fit-content",
                                            background: `${selected ? palette.primary.main : "none"}`,
                                            transition: "background 200ms",
                                            borderColor: selected ? palette.primary.light : palette.borderColor,
                                            color: selected ? palette.common.white : palette.text.secondary,
                                            cursor: "pointer",
                                            userSelect: "none",

                                            ":hover": {
                                                borderColor: selected ? palette.primary.light : palette.primary.light,
                                                color: selected ? palette.common.white : palette.text.primary
                                            }
                                        }}>
                                        {questionDetails.correct_answers?.length > 1 && <Box
                                            component="span"
                                            sx={{
                                                marginLeft: "-10px",
                                                textAlign: "left",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: 'center',
                                                justifyContent: "center",
                                                color: 'inherit',
                                                height: "30px",
                                                borderRadius: "50%"
                                            }}>
                                            <Checkbox
                                                sx={{
                                                    color: "GrayText",
                                                    '&.Mui-checked': {
                                                        color: "white",
                                                    },
                                                }}
                                                checked={Boolean( selected ) || false} />
                                        </Box>}
                                        {questionDetails.correct_answers?.length === 1 && <Box
                                            component="span"
                                            sx={{
                                                marginLeft: "-10px",
                                                textAlign: "left",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: 'center',
                                                justifyContent: "center",
                                                color: 'inherit',
                                                height: "30px",
                                                borderRadius: "50%"
                                            }}>
                                            <Radio
                                                sx={{
                                                    color: "GrayText",
                                                    '&.Mui-checked': {
                                                        color: "white",
                                                    },
                                                }}
                                                name={`question-${SimbaAIStore.getQuizState.questionIndex}`}
                                                value={`option-${index}`}
                                                checked={Boolean( selected ) || false}
                                            />
                                        </Box>}
                                        <Typography
                                            component="pre"
                                            sx={{
                                                textAlign: "left",
                                                fontSize: { md: "18px", xs: "14px" },
                                                color: 'inherit',
                                                fontWeight: "600",
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                userSelect: "none"
                                            }}>{option}</Typography>
                                    </Box>
                                );
                            } )}
                        </Box>
                    </Box>
                </Box>
            </Slide>
            <Box
                sx={{
                    display: "flex",
                    position: "sticky",
                    bottom: "0",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: `4px dashed ${palette.borderColor}`,
                    bgcolor: palette.contentBg,
                    padding: { md: "30px 20px", xs: "10px" }
                }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                    <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' color='error' >Quit Practice</Button>
                    <ReportIssue key={SimbaAIStore.getQuizState.questionIndex} />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>

                    {( SimbaAIStore.getQuizState.questionIndex < SimbaAIStore.getPracticeSessionQuestions.length - 1 ) && questionDetails.correct_answers.length > 1 && <Button disabled={!SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] || SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex].length < 2} onClick={nextQuestion} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} endIcon={Icons.default.ArrowForward} variant='contained' disableElevation color='primary' >Next</Button>}
                    {( SimbaAIStore.getQuizState.questionIndex < SimbaAIStore.getPracticeSessionQuestions.length - 1 ) && questionDetails.correct_answers.length === 1 && <Button disabled={!SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex]} onClick={nextQuestion} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} endIcon={Icons.default.ArrowForward} variant='contained' disableElevation color='primary' >Next</Button>}
                    {( SimbaAIStore.getQuizState.questionIndex >= SimbaAIStore.getPracticeSessionQuestions.length - 1 ) && <Button disabled={submitting || !SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex]} className={SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ? 'animation-wave' : ""} onClick={submitAndViewScores} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} startIcon={submitting ? <CircularProgress size={20} /> : Icons.default.CheckIcon} variant='contained' disableElevation color='primary' >{submitting ? "Submitting" : "Submit"}</Button>}
                </Box>
            </Box>
        </Box>
    );
}
)
export default QuizQuestion
